import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { formatApiErrorDetail } from "../lib/operatorWalk";

export default function AdminLogin() {
  const { user, login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Already authenticated: admins go to the dashboard, clients to their portal.
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      if (u.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        // Clients are not permitted in the admin area.
        navigate("/portal", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-900 flex items-center justify-center px-6 py-12" data-testid="page-admin-login">
      <div className="w-full max-w-md bg-white border border-brand-800">
        <div className="p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-brand-700 text-white flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <div className="font-serif text-xl text-brand-700 leading-tight">Admin Login</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-muted">Holder Hospitality Consulting</div>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                autoComplete="email"
                className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600" data-testid="admin-login-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-sm font-medium transition-colors"
              data-testid="admin-login-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-8 text-xs text-ink-muted text-center">
            Client?{" "}
            <Link to="/portal/login" className="text-brand-700 underline" data-testid="admin-login-client-link">
              Use the client login
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
